import categoriesData from '../utils/categories.json';

/**
 * Convert category name to URL-friendly slug
 * @param categoryName - The category name (e.g., "Fashion & Apparel")
 * @returns URL-friendly slug (e.g., "fashion-and-apparel")
 */

export const categoryToSlug = (categoryName: string): string => {
  return categoryName
    .toLowerCase()
    .replace(/,/g, '')
    .replace(/\s+/g, '-')
    .replace(/&/g, 'and');
};

/**
 * Convert URL slug back to category name
 * @param slug - The URL slug (e.g., "fashion-and-apparel")
 * @returns Original category name format (e.g., "Fashion & Apparel")
 */
export const slugToCategory = (slug: string): string => {
  return slug
    .replace(/-and-/g, ' & ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Generate category URL path
 * @param categoryName - The category name
 * @returns Complete URL path for the category
 */
export const getCategoryUrl = (categoryName: string): string => {
  return `/giftcards/${categoryToSlug(categoryName)}`;
};

/**
 * Check if a slug matches a category name
 * @param slug - The URL slug to check
 * @param categoryName - The category name to match against
 * @returns true if the slug matches the category
 */
export const isSlugForCategory = (slug: string, categoryName: string): boolean => {
  return categoryToSlug(categoryName) === slug;
};

/**
 * Find category name from slug in a list of categories
 * @param slug - The URL slug to find
 * @param categories - Array of category names
 * @returns The matching category name or null if not found
 */
export const findCategoryBySlug = (slug: string, categories: string[]): string | null => {
  return categories.find(category => isSlugForCategory(slug, category)) || null;
};

/**
 * Convert slug to backend category name for API requests
 * @param slug - The URL slug (e.g., "retail-and-shopping")
 * @returns Backend category name (e.g., "Retail & Shopping") or the slug if not found
 */
export const slugToBackendCategory = (slug: string): string => {
  const category = categoriesData.find((cat: { Slug: string; Name: string }) => cat.Slug === slug);
  return category?.Name || slug;
};
